import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { PiEyeSlashLight, PiEyeThin } from "react-icons/pi";
import { MdOutlineCancel } from "react-icons/md";
import { RotatingLines } from "react-loader-spinner";
import toast, { Toaster } from "react-hot-toast";
import Modal from "../Modal/Modal";
import Logo from "../../Images/Logo.svg";
import { openModalAction } from "../../Redux/actions/modal";
import { loginAction, loginFailureAction } from "../../Redux/actions/auth";

const Login = () => {
  const [showPassword, setShowPassword] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [loading, setLoading] = useState(false);
  const isModalOpen = useSelector((state) => state.modal.isModalOpen);
  const error = useSelector((state) => state.auth.error);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const openModalHandler = () => {
    dispatch(openModalAction());
  };

  const validateForm = () => {
    let valid = true;

    // Email validation
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.match(emailPattern)) {
      setEmailError("Invalid email format");
      valid = false;
    } else {
      setEmailError("");
    }

    // Password validation
    if (!password) {
      setPasswordError("Password cannot be empty");
      valid = false;
    } else {
      setPasswordError("");
    }

    return valid;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      console.log("Attempting to log in with:", { email, password });

      const response = await fetch("https://backend.pmc-group.uk/api/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
          password: password,
        }),
      });

      const data = await response.json();
      console.log("Response from API:", data);

      if (response.ok) {
        console.log("API call successful", data);

        localStorage.setItem("token", data.authorization.access_token);

        dispatch(loginAction(data.user, data.authorization));

        toast.success("Login successful!");
        navigate("/dashboard");
      } else {
        console.log("API call failed", data);
        const errorMessage = data.error ? data.error.message : "Login failed";
        toast.error(errorMessage);
        dispatch(loginFailureAction(errorMessage));
      }
    } catch (error) {
      console.error("Network error", error);
      toast.error("Network error. Please try again.");
      dispatch(
        loginFailureAction({ message: error.message || "Network error" })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex md:justify-between justify-center md:ml-20 ">
        <Link to="/">
          <div className="xl:w-[300px] cursor-pointer">
            <img src={Logo} alt="" className="w-[150px]" />
          </div>
        </Link>

        <Link to="/">
          <div className="absolute top-4 right-8 cursor-pointer">
            <MdOutlineCancel className="text-2xl hidden md:block" />
          </div>
        </Link>
      </div>
      <div className="flex justify-center">
        {isModalOpen && <Modal />}
        <div className="relative flex w-full max-w-md flex-col space-y-5 rounded-lg mt-8 mb-14 border bg-white px-5 py-6 shadow-xl sm:mx-auto">
          <div className="-z-10 absolute top-4 left-1/2 h-full w-5/6 -translate-x-1/2 rounded-lg sm:-right-10 sm:top-auto sm:left-auto sm:w-full sm:translate-x-0"></div>
          <div className="mx-auto mb-2 space-y-3">
            <h1 className="text-center text-3xl font-bold text-gray-700">
              Login
            </h1>
            <p className="text-gray-500">Login to access your account</p>
          </div>

          {error && (
            <p className="text-red-500 text-center">
              {typeof error === "string" ? error : "An error occurred"}
            </p>
          )}

          <div>
            <div className="relative mt-2 w-full">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-1 peer block w-full appearance-none rounded-lg border border-gray-300 bg-transparent px-2.5 pt-4 pb-2.5 text-sm text-gray-900 focus:border-blue-600 focus:outline-none focus:ring-0"
                placeholder=" "
              />
              <label
                htmlFor="email"
                className="origin-[0] peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2 peer-focus:text-blue-600 absolute left-1 top-2 z-10 -translate-y-4 scale-75 transform cursor-text select-none bg-white px-2 text-sm text-gray-500 duration-300"
              >
                Enter Your Email
              </label>
              {emailError && (
                <p className="text-red-500 text-sm mt-1">{emailError}</p>
              )}
            </div>
          </div>

          <div>
            <div className="relative mt-2 w-full">
              <input
                type={showPassword}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                id="password"
                className="border-1 peer block w-full appearance-none rounded-lg border border-gray-300 bg-transparent px-2.5 pt-4 pb-2.5 text-sm text-gray-900 focus:border-blue-600 focus:outline-none focus:ring-0"
                placeholder=" "
              />
              <label
                htmlFor="password"
                className="origin-[0] peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2 peer-focus:text-blue-600 absolute left-1 top-2 z-10 -translate-y-4 scale-75 transform cursor-text select-none bg-white px-2 text-sm text-gray-500 duration-300"
              >
                Enter Your Password
              </label>
              {showPassword === "password" ? (
                <div
                  onClick={() => setShowPassword("text")}
                  className="absolute top-4 right-2 cursor-pointer"
                >
                  <PiEyeSlashLight />
                </div>
              ) : (
                <div
                  onClick={() => setShowPassword("password")}
                  className="absolute top-4 right-2 cursor-pointer"
                >
                  <PiEyeThin />
                </div>
              )}
              {passwordError && (
                <p className="text-red-500 text-sm mt-1">{passwordError}</p>
              )}
            </div>
          </div>
          <div className="flex w-full items-center">
            <button
              onClick={handleLogin}
              className="shrink-0 inline-block w-36 rounded-lg bg-navBg py-3 font-bold text-white"
              disabled={loading}
            >
              <div className="button-content">
                {loading ? (
                  <RotatingLines
                    strokeColor="#fff"
                    strokeWidth="5"
                    animationDuration="0.75"
                    width="20"
                    visible={true}
                  />
                ) : (
                  "Login"
                )}
              </div>
            </button>

            <span
              className="w-full text-center text-sm font-medium text-gray-600 hover:underline cursor-pointer"
              onClick={() => navigate("/forgot-password")}
            >
              Forgot your password?
            </span>
          </div>
          <p className="text-center text-gray-600">
            Don't have an account?
            <span
              className="underline text-navBg cursor-pointer pl-2"
              onClick={openModalHandler}
            >
              Sign up
            </span>
          </p>
        </div>
      </div>
    </>
  );
};

export default Login;
